/**
 * A single staged (uploaded-but-not-yet-promoted) template version row for
 * TemplateDetail's Versions card — rendered above the numbered version
 * history. Shows the "Staged — awaiting review" badge, uploader/changeNotes,
 * a staleness flag when the template's clause set has moved on since this
 * sample was rendered, and Download sample / Promote / Discard actions.
 *
 * Promote/discard/download-sample failures render locally (the staged record
 * is untouched by any of the three, so the row that triggered the failure is
 * still there to show the error) via the same structured-400/generic-error
 * split used by TemplateUploadModal. A successful promote's `extractionWarning`
 * is NOT shown here — the staged record is deleted as part of activation, so
 * the row itself is gone by the time the caller could render anything; the
 * caller (TemplateDetail) lifts that message to a card-level banner instead.
 */

import { useState, useRef, useEffect } from 'react';
import { Download, Check, Trash2 } from 'lucide-react';
import { Button } from '../common';
import TagPreview from './TagPreview';
import { getTagValidationIssues } from '../../services/api';
import type { StagedTemplateVersion, TagIssue } from '../../types/orgTemplate';

interface StagedVersionRowProps {
  staged: StagedTemplateVersion;
  isStale: boolean;
  onDownloadSample: (stagedId: string) => Promise<void> | void;
  onPromote: (stagedId: string) => Promise<{ version: number; extractionWarning?: string }>;
  onDiscard: (stagedId: string) => Promise<void>;
  /** THIS row's promote is in flight (drives the button spinner). */
  isPromoting: boolean;
  /** THIS row's discard is in flight (drives the button spinner). */
  isDiscarding: boolean;
  /** ANY staged action is in flight anywhere — disables this row's actions
   *  (without a spinner) so concurrent activations can't race. */
  actionsDisabled: boolean;
}

export default function StagedVersionRow({
  staged,
  isStale,
  onDownloadSample,
  onPromote,
  onDiscard,
  isPromoting,
  isDiscarding,
  actionsDisabled,
}: StagedVersionRowProps) {
  const [tagErrors, setTagErrors] = useState<TagIssue[]>([]);
  const [genericError, setGenericError] = useState<string | null>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  // Move focus to the error region as soon as a rejected promote/discard
  // populates it, so a keyboard or screen-reader user is taken straight to
  // what went wrong (same idiom as TemplateUploadModal's alert).
  useEffect(() => {
    if (tagErrors.length > 0 || genericError) {
      alertRef.current?.focus();
    }
  }, [tagErrors, genericError]);

  const handlePromote = async () => {
    setTagErrors([]);
    setGenericError(null);
    try {
      await onPromote(staged.stagedId);
      // On success the staged record is deleted and this row unmounts once
      // the versions query refetches — nothing further to do here.
    } catch (err) {
      const issues = getTagValidationIssues(err);
      if (issues) {
        setTagErrors(issues);
        return;
      }
      setGenericError(err instanceof Error ? err.message : 'Promote failed. Please try again.');
    }
  };

  const handleDiscard = async () => {
    if (!confirm('Discard this staged upload? This cannot be undone.')) return;
    setTagErrors([]);
    setGenericError(null);
    try {
      await onDiscard(staged.stagedId);
    } catch (err) {
      setGenericError(err instanceof Error ? err.message : 'Discard failed. Please try again.');
    }
  };

  const handleDownloadSample = async () => {
    setTagErrors([]);
    setGenericError(null);
    try {
      await onDownloadSample(staged.stagedId);
    } catch (err) {
      setGenericError(
        err instanceof Error ? err.message : 'Could not open the sample. Please try again.',
      );
    }
  };

  return (
    <div className="p-3 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-md space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <span className="badge-pending">Staged — awaiting review</span>
          {staged.changeNotes && (
            <span className="ml-3 text-body-sm text-slate-500 dark:text-slate-400">
              {staged.changeNotes}
            </span>
          )}
          <span className="ml-3 text-caption text-slate-400">
            {new Date(staged.uploadedAt).toLocaleDateString()}
          </span>
          <span className="ml-2 text-caption text-slate-400 dark:text-slate-500">
            by {staged.uploadedBy}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            icon={<Download className="h-3 w-3" />}
            onClick={handleDownloadSample}
          >
            Download sample
          </Button>
          <Button
            variant="danger"
            size="sm"
            icon={<Trash2 className="h-3 w-3" />}
            onClick={handleDiscard}
            loading={isDiscarding}
            disabled={actionsDisabled && !isDiscarding}
          >
            Discard
          </Button>
          <Button
            size="sm"
            icon={<Check className="h-3 w-3" />}
            onClick={handlePromote}
            loading={isPromoting}
            disabled={actionsDisabled && !isPromoting}
          >
            Promote
          </Button>
        </div>
      </div>

      {isStale && (
        <div
          role="status"
          className="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-100 dark:bg-amber-900/30 px-3 py-2 text-body-sm text-amber-800 dark:text-amber-200"
        >
          Clause set changed since this sample was rendered — re-stage to refresh.
        </div>
      )}

      {(tagErrors.length > 0 || genericError) && (
        <div
          ref={alertRef}
          role="alert"
          tabIndex={-1}
          className="rounded-md border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-body-sm text-red-800 dark:text-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
        >
          {genericError ? (
            <p>{genericError}</p>
          ) : (
            <>
              <p className="font-medium mb-2">
                This version was not activated. Fix the clause markup below, then re-stage.
              </p>
              <ul className="list-disc pl-5 space-y-1">
                {tagErrors.map((issue, i) => (
                  <li key={`${issue.code}-${issue.tag ?? i}`}>
                    {issue.tag && <code className="font-mono">{`{${issue.tag}}`}</code>}
                    {issue.tag ? ' — ' : ''}
                    {issue.message}
                    {issue.suggestion && (
                      <span className="mt-0.5 flex items-center gap-1.5">
                        <span className="font-medium">Suggested:</span>
                        <TagPreview
                          tag={issue.suggestion}
                          variant="plain"
                          label={`suggested tag {${issue.suggestion}}`}
                        />
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
